import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import Fuse from 'fuse.js';
import { Router } from '@angular/router';

declare global {
  interface Window {
    webkitSpeechRecognition: any;
    SpeechRecognition: any;
  }
}

@Injectable({
  providedIn: 'root'
})
export class SpeechRecognitionService {
  private recognition: any;
  private isRecognitionSupported: boolean = false;
  private speechPermissionGranted: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private detectedTextSubject: Subject<string> = new Subject<string>();
  private commandSubject: Subject<{ command: string, payload?: any }> = new Subject<{ command: string, payload?: any }>();
  private curaInputSubject: Subject<string> = new Subject<string>();
  private isRecognitionActive: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private forceStop: boolean = false;

  private commandsMapping: { [command: string]: string } = {
    'open recording': 'OPEN_RECORDING',
    'start recording': 'START_RECORDING',
    'pause recording': 'PAUSE_RECORDING',
    'stop recording': 'STOP_RECORDING',
    'ask cura': 'OPEN_CURA',
    'open cura': 'OPEN_CURA',
    'close cura': 'CLOSE_CURA',
    'add invite': 'ADD_INVITE',
    'create invite': 'ADD_INVITE',
    'add inpatient': 'ADD_INPATIENT',
    'create inpatient': 'ADD_INPATIENT',
    'add outpatient': 'ADD_OPD',
    'create outpatient': 'ADD_OPD',
    'add opd': 'ADD_OPD',
    'create opd': 'ADD_OPD',
    'close file': 'CLOSE_FILE',
    'close window': 'CLOSE_FILE',
    'close page': 'CLOSE_FILE',
    'close ai request': 'CLOSE_FILE',
    'close request': 'CLOSE_FILE',
    'close ai': 'CLOSE_FILE',
    'yes': 'CONFIRM',
    'confirm': 'CONFIRM',
    'ok': 'CONFIRM',
    'okay': 'CONFIRM',
    'no': 'DENY',
    'cancel': 'DENY',
    'deny': 'DENY',
  };

  private urlMappings: { [urlSegment: string]: string[] } = {
    'file': ['details', 'timeline', 'ai_request', 'prescription', 'appointments', 'tasks', 'billing', 'consent', 'communication', 'media', 'tags'],
    'edit-request': ['top', 'pc', 'file_update', 'medication', 'tasks', 'consent', 'lom', 'letter_to_colleague', 'letter_to_gp', 'letter_to_patient', 'letter_to_patient_employer']
  };

  private scrollToSections = [
    { id: 'details', name: 'details', synonyms: ['info', 'information', 'demographics', 'top'] },
    { id: 'timeline', name: 'timeline', synonyms: ['history', 'events', 'notes'] },
    { id: 'ai_request', name: 'AI request', synonyms: ['ai', 'artificial intelligence', 'request'] },
    { id: 'prescription', name: 'prescription', synonyms: ['medications', 'drugs', 'rx', 'medication', 'prescriptions'] },
    { id: 'appointments', name: 'appointments', synonyms: ['meetings', 'consultations', 'schedule'] },
    { id: 'tasks', name: 'tasks', synonyms: ['to-do', 'actions', 'task list'] },
    { id: 'billing', name: 'billing', synonyms: ['payments', 'invoices', 'codes', 'coding'] },
    { id: 'consent', name: 'consent', synonyms: ['permissions', 'agreements', 'treatment consent'] },
    { id: 'communication', name: 'communication', synonyms: ['messages', 'contact'] },
    { id: 'media', name: 'media', synonyms: ['photos', 'images', 'videos'] },
    { id: 'tags', name: 'tags', synonyms: ['labels', 'categories'] },
    { id: 'top', name: 'top', synonyms: ['notes', 'overview', 'summary'] },
    { id: 'pc', name: 'procedure codes', synonyms: ['pc', 'procedure codes', 'codes', 'coding'] },
    { id: 'file_update', name: 'file update', synonyms: ['update', 'record update', 'file'] },
    { id: 'medication', name: 'medication', synonyms: ['prescriptions', 'medicine', 'pharmacy'] },
    { id: 'lom', name: 'letter of motivation', synonyms: ['lom', 'motivation letter', 'letter of motivation'] },
    { id: 'letter_to_colleague', name: 'letter to colleague', synonyms: ['referral letter', 'consultant letter', 'colleague letter'] },
    { id: 'letter_to_gp', name: 'letter to GP', synonyms: ['general practitioner letter', 'gp letter'] },
    { id: 'letter_to_patient', name: 'letter to patient', synonyms: ['patient letter', 'message to patient'] },
    { id: 'letter_to_patient_employer', name: 'letter to patient employer', synonyms: ['sick letter', 'medical certificate', 'employer letter'] }
  ];

  private fuseOptions = {
    keys: ['name', 'synonyms'],
    threshold: 0.3,
    includeScore: true,
    shouldSort: true
  };

  private navToSections = [
    { id: 'schedule', name: 'schedule', synonyms: ['schedule', 'calendar', 'appointments', 'top'] },
    { id: 'list', name: 'list', synonyms: ['list', 'patients', 'opd', 'inpatient', 'invite'] },
    { id: 'filters', name: 'filters', synonyms: ['tasks', 'logbook', 'procedures'] },
    { id: 'coding', name: 'coding', synonyms: ['billing', 'procedure codes'] },
  ];


  private navFuse = new Fuse(this.navToSections, this.fuseOptions);


  constructor(private ngZone: NgZone, private router: Router) {
    // Check if the browser supports speech recognition
    this.isRecognitionSupported = 'webkitSpeechRecognition' in window || 'SpeechRecognition' in window;
  }

  async init() {
    if (!this.recognition) {
      // Check and request microphone permission if needed
      const permissionGranted = await this.requestMicrophonePermission();
      if (!permissionGranted) {
        console.warn('Microphone permission is not granted.');
        return false;
      }

      // Initialize speech recognition after permissions are confirmed
      this.recognition = new (window.webkitSpeechRecognition || window.SpeechRecognition)();
      this.recognition.continuous = true;
      this.recognition.interimResults = true;
      this.recognition.lang = 'en-ZA';

      // Handle recognition result
      this.recognition.onresult = (event: any) => {
        let detectedText = '';
        for (let i = event.resultIndex; i < event.results.length; ++i) {
          detectedText += event.results[i][0].transcript.trim() + ' ';
        }
        detectedText = detectedText.trim();

        this.ngZone.run(() => {
          if (event.results[event.results.length - 1].isFinal) {
            this.detectedTextSubject.next(detectedText);
            this.handleCommand(detectedText);
            this.handleCuraInput(detectedText);
          }
        });
      };

      // Handle recognition errors
      this.recognition.onerror = (event: any) => {
        console.error('Speech recognition error', event);
      };

      // Handle start and end events for recognition
      this.recognition.onstart = () => {
        this.ngZone.run(() => {
          this.isRecognitionActive.next(true);
        });
      };

      this.recognition.onend = () => {
        this.ngZone.run(() => {
          if (!this.forceStop) {
            this.startRecognition();
          } else {
            this.isRecognitionActive.next(false);
          }
        });
      };
    }

    return true;
  }

  // Request microphone permission from the user
  private async requestMicrophonePermission(): Promise<boolean> {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      this.speechPermissionGranted.next(true);
      stream.getTracks().forEach(track => track.stop()); // Stop all tracks after permission is granted
      return true;
    } catch (error) {
      console.error('Microphone permission denied', error);
      this.speechPermissionGranted.next(false);
      return false;
    }
  }

  // Check if the device/browser supports speech recognition
  isSpeechRecognitionSupported(): boolean {
    return this.isRecognitionSupported;
  }

  // Observable to check if microphone permissions are granted
  isMicrophonePermissionGranted(): Observable<boolean> {
    return this.speechPermissionGranted.asObservable();
  }

  // Start speech recognition
  async startRecognition(): Promise<void> {
    if (this.isRecognitionSupported) {
      await this.init();
      this.forceStop = false;
      this.recognition.start();
    } else {
      console.warn('Speech recognition is not supported on this device/browser.');
    }
  }

  // Stop speech recognition
  stopRecognition(): void {
    if (this.isRecognitionSupported) {
      this.forceStop = true;
      this.recognition.stop();
    }
  }

  // Observable to check if speech recognition is active
  isActive(): Observable<boolean> {
    return this.isRecognitionActive.asObservable();
  }

  // Broadcast detected text to other components/services
  getDetectedText(): Observable<string> {
    return this.detectedTextSubject.asObservable();
  }

  // Broadcast detected commands to other components/services
  getDetectedCommand(): Observable<{ command: string, payload?: any }> {
    return this.commandSubject.asObservable();
  }

  // Broadcast Cura input text to other components/services
  getCuraInput(): Observable<string> {
    return this.curaInputSubject.asObservable();
  }

  // Map detected text to commands and broadcast the command if found
  private handleCommand(detectedText: string): void {

    // Handling specific commands with the payload for "scroll to" or "move to"
    const scrollToPrefix = this.findBestMatchingPrefix(detectedText.toLowerCase(), ['scroll to', 'move to']);
    if (scrollToPrefix) {
      const scrollTarget = detectedText.split(' ').slice(2).join(' ');
      if (scrollTarget) {
        const scrollLocation = this.findScrollSection(scrollTarget);
        if (scrollLocation) {
          this.commandSubject.next({ command: 'SCROLL_TO', payload: scrollLocation });
          return;
        }
      }
    }

    // Handling specific commands with payload for "load patient" or "open patient"
    const loadPatientPrefix = this.findBestMatchingPrefix(detectedText.toLowerCase(), ['load patient', 'open patient', 'open file', 'load file']);
    //console.log('loadPatientPrefix', loadPatientPrefix)
    if (loadPatientPrefix) {
      const patientName = detectedText.split(' ').slice(2).join(' ');
      //console.log('patientName', patientName);
      if (patientName) {
        this.commandSubject.next({ command: 'LOAD_PATIENT', payload: patientName });
        return;
      }
    }

    // Handling specific commands with payload for "go to" or "navigate to"
    const mainNavPrefix = this.findBestMatchingPrefix(detectedText.toLowerCase(), ['go to', 'navigate to']);
    //console.log('mainNavPrefix', mainNavPrefix)
    if (mainNavPrefix) {
      const navTarget = detectedText.split(' ').slice(2).join(' ');
      //console.log('navName', navName);
      if (navTarget) {
        const navLocation = this.findNavLocation(navTarget);
        if (navLocation) {
          this.commandSubject.next({ command: 'GO_TO', payload: navLocation });
          return;
        }
      }
    }

    // Check for the "generate" command within the "edit-request" URL context
    if (this.router.url.includes('edit-request') && detectedText.toLowerCase().includes('generate')) {
      const generateTarget = detectedText.split(' ').slice(1).join(' '); // Capture what follows "generate"
      const generateLocation = this.findScrollSection(generateTarget);

      if (generateLocation) {
        this.commandSubject.next({ command: 'GENERATE_SECTION', payload: generateLocation });
        return;
      }
    }

    const loadAiIndex = this.detectLoadAiRequestCommand(detectedText);
    if (loadAiIndex !== null) {
      this.commandSubject.next({ command: 'LOAD_REQUEST_BY_POSITION', payload: loadAiIndex });
      return;
    }

    const date = this.parseDateCommand(detectedText);
    if (date) {
      this.commandSubject.next({ command: 'SHOW_APPOINTMENTS', payload: date });
      return;
    }

    const bestMatch = this.findBestMatchingCommand(detectedText.toLowerCase());
    if (bestMatch) {
      this.commandSubject.next({ command: bestMatch });
    }
  }

  // Handle Cura-specific input
  private handleCuraInput(detectedText: string): void {
    if (this.commandsMapping['open cura'] && detectedText.toLowerCase() !== 'open cura') {
      this.curaInputSubject.next(detectedText);
    }
  }

  // Utility function to find the best matching command using similarity scoring
  private findBestMatchingCommand(detectedText: string): string | null {
    let bestMatch: string | null = null;
    let highestSimilarity = 0.7; // Similarity threshold

    for (const [command, commandValue] of Object.entries(this.commandsMapping)) {
      const similarity = this.calculateSimilarity(detectedText, command);
      if (similarity > highestSimilarity) {
        highestSimilarity = similarity;
        bestMatch = commandValue;
      }
    }

    return bestMatch;
  }

  private detectLoadAiRequestCommand(detectedText: string): number | null {
    const lowerText = detectedText.toLowerCase();
    console.log(lowerText, lowerText.includes("load"), lowerText.includes("ai"))
    // Ensure the command contains both "load" and "AI"
    if (!(lowerText.includes("load") && lowerText.includes("ai")) && !(lowerText.includes("open") && lowerText.includes("ai"))) {
      return null;
    }

    // Map of positional terms to list index positions
    const positionMap: { [key: string]: number } = {
      'first': 0, '1st': 0,
      'second': 1, '2nd': 1,
      'third': 2, '3rd': 2,
      'fourth': 3, '4th': 3,
      'fifth': 4, '5th': 4,
      'sixth': 5, '6th': 5,
      'seventh': 6, '7th': 6,
      'eighth': 7, '8th': 7,
      'ninth': 8, '9th': 8,
      'tenth': 9, '10th': 9,
      'eleventh': 10, '11th': 10,
      'twelfth': 11, '12th': 11,
      'thirteenth': 12, '13th': 12,
      'fourteenth': 13, '14th': 13,
      'fifteenth': 14, '15th': 14,
      'sixteenth': 15, '16th': 15,
      'seventeenth': 16, '17th': 16,
      'eighteenth': 17, '18th': 17,
      'nineteenth': 18, '19th': 18,
      'twentieth': 19, '20th': 19,
      'last': -1,       // Last item
      'most recent': 0  // Most recent means the first item in a sorted recent-first list
    };



    console.log('searching positionMap: ', positionMap);
    // Detect the position in the detected text
    for (const [key, index] of Object.entries(positionMap)) {
      console.log('lowerText.includes ', key, lowerText);
      if (lowerText.includes(key)) {
        console.log('found at index ', index);
        return index;
      }
    }

    // No positional keyword matched
    console.log('No positional keyword matched');
    return null;
  }

  private parseDateCommand(detectedText: string): Date | null {
    console.log('parseDateCommand', detectedText)
    const today = new Date();
    const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const monthsOfYear = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

    // Helper to convert text numbers (e.g., "one") to digits
    const textToNumberMap: { [key: string]: number } = {
      'one': 1, 'two': 2, 'three': 3, 'four': 4, 'five': 5, 'six': 6, 'seven': 7, 'eight': 8, 'nine': 9, 'ten': 10,
      'eleven': 11, 'twelve': 12, 'thirteen': 13, 'fourteen': 14, 'fifteen': 15, 'sixteen': 16, 'seventeen': 17,
      'eighteen': 18, 'nineteen': 19, 'twenty': 20, 'thirty': 30, 'forty': 40, 'fifty': 50
    };

    const toDigit = (word: string): number | null => textToNumberMap[word.toLowerCase()] || null;

    // Check if the command is for "today"
    if (detectedText.toLowerCase().includes('today')) {
      return today;
    }

    // Handle "next" keyword for days of the week, e.g., "next Friday"
    const nextMatch = detectedText.toLowerCase().includes('next');
    const dayOfWeekMatch = daysOfWeek.find(day => detectedText.toLowerCase().includes(day.toLowerCase()));

    if (dayOfWeekMatch) {
      const targetDayIndex = daysOfWeek.indexOf(dayOfWeekMatch);
      let daysUntilTarget = targetDayIndex - today.getDay();

      if (daysUntilTarget < 0 || nextMatch) {
        daysUntilTarget += 7;  // Move to the next week if "next" is mentioned or it's before today
      }

      const targetDate = new Date(today);
      targetDate.setDate(today.getDate() + daysUntilTarget);
      return targetDate;
    }

    // Handle "five October 2024" or "2 September 2023"
    const monthDayYearRegex = /\b(\d{1,2}|one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve)\s?([A-Za-z]+)\s?(\d{4})?\b/;
    const monthDayYearMatch = detectedText.match(monthDayYearRegex);
    if (monthDayYearMatch) {
      const dayText = monthDayYearMatch[1];
      const monthName = monthDayYearMatch[2];
      const year = monthDayYearMatch[3] ? Number(monthDayYearMatch[3]) : today.getFullYear();
      const monthIndex = monthsOfYear.findIndex(month => month.toLowerCase() === monthName.toLowerCase());

      if (monthIndex === -1) return null;

      const day = isNaN(Number(dayText)) ? toDigit(dayText.toLowerCase()) : Number(dayText);
      if (day === null) return null;

      return new Date(year, monthIndex, day);
    }

    // Handle "6 July" or "July 6" without year
    const monthDayRegex = /\b(\d{1,2})\s([A-Za-z]+)|([A-Za-z]+)\s(\d{1,2})\b/;
    const monthDayMatch = detectedText.match(monthDayRegex);
    if (monthDayMatch) {
      const day = monthDayMatch[1] || monthDayMatch[4];
      const monthName = monthDayMatch[2] || monthDayMatch[3];
      const monthIndex = new Date(Date.parse(`${monthName} 1`)).getMonth();
      const targetDate = new Date(today.getFullYear(), monthIndex, Number(day));

      // If this date is before today, assume the user meant next year
      if (targetDate < today) {
        targetDate.setFullYear(today.getFullYear() + 1);
      }
      return targetDate;
    }

    // Handle cases like "1 January 2025"
    const fullDateRegex = /\b(\d{1,2})\s([A-Za-z]+)\s(\d{4})|\b([A-Za-z]+)\s(\d{1,2}),?\s(\d{4})\b/;
    const fullDateMatch = detectedText.match(fullDateRegex);
    if (fullDateMatch) {
      const day = fullDateMatch[1] || fullDateMatch[5];
      const monthName = fullDateMatch[2] || fullDateMatch[4];
      const year = fullDateMatch[3] || fullDateMatch[6];
      const monthIndex = monthsOfYear.findIndex(month => month.toLowerCase() === monthName.toLowerCase());

      if (monthIndex === -1) return null;

      return new Date(Number(year), monthIndex, Number(day));
    }

    return null;
  }

  private findScrollSection(term: string): string | null {
    const allowedSections = this.getAllowedSections();
    const filteredScrollFuse = new Fuse(allowedSections, this.fuseOptions); // Use a new Fuse instance with allowed sections

    const results = filteredScrollFuse.search(term);
    return results.length > 0 ? results[0].item.id : null;
  }

  private getAllowedSections(): { id: string; name: string; synonyms: string[] }[] {
    const url = this.router.url;
    let matchedKey = '';

    // Check if any of the specific URL segments are present in the URL
    for (const key in this.urlMappings) {
      if (url.includes(key)) {
        matchedKey = key;
        break;
      }
    }

    // Filter `scrollToSections` based on the allowed sections in `urlMappings`
    return this.scrollToSections.filter(section => this.urlMappings[matchedKey]?.includes(section.id));
  }


  private findNavLocation(term: string): string | null {
    const results = this.navFuse.search(term);
    return results.length > 0 ? results[0].item.id : null;
  }


  // Utility function to find the best matching prefix for patient commands
  private findBestMatchingPrefix(detectedText: string, prefixes: string[]): string | null {
    let bestMatch: string | null = null;
    let highestSimilarity = 0.7;

    const firstTwoWords = detectedText.split(' ').slice(0, 2).join(' ');

    for (const prefix of prefixes) {
      const similarity = this.calculateSimilarity(firstTwoWords, prefix);
      if (similarity > highestSimilarity) {
        highestSimilarity = similarity;
        bestMatch = prefix;
      }
    }
    return bestMatch;
  }


  // Calculate similarity between two strings (basic implementation)
  private calculateSimilarity(str1: string, str2: string): number {
    const lengthDifference = Math.abs(str1.length - str2.length);
    const commonLength = Math.min(str1.length, str2.length);
    let matchingCharacters = 0;

    for (let i = 0; i < commonLength; i++) {
      if (str1[i] === str2[i]) {
        matchingCharacters++;
      }
    }

    return (matchingCharacters - lengthDifference) / Math.max(str1.length, str2.length);
  }

}
